import { eachDayOfInterval, format, isSameMonth, isValid, parseISO } from 'date-fns';
import { cs } from 'date-fns/locale';
import { ReactNode, createContext, useContext, useMemo } from 'react';

const MAX_DAYS = 366;

interface DashboardRangeValue {
  range: [string, string];
  days: Date[];
  label: string;
  /** First day of the month when the range is inside a single month, otherwise null. */
  month: Date | null;
  isCurrentMonth: boolean;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const buildValue = (range: [string, string]): DashboardRangeValue => {
  const from = parseISO(range[0]);
  const to = parseISO(range[1]);
  const valid = isValid(from) && isValid(to) && from <= to;
  const month = valid && isSameMonth(from, to) ? from : null;
  const days = valid ? eachDayOfInterval({ start: from, end: to }).slice(0, MAX_DAYS) : [];
  const isFullMonth = month !== null && from.getDate() === 1 && !isSameMonth(new Date(to.getTime() + 86_400_000), to);
  const label = !valid
    ? ''
    : isFullMonth
      ? capitalize(format(from, 'LLLL yyyy', { locale: cs }))
      : `${format(from, 'dd.MM.yyyy')} - ${format(to, 'dd.MM.yyyy')}`;
  return { range, days, label, month, isCurrentMonth: month !== null && isSameMonth(month, new Date()) };
};

const DashboardRangeContext = createContext<DashboardRangeValue>(
  buildValue([format(new Date(), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')])
);

export const DashboardRangeProvider = ({ range, children }: { range: [string, string]; children: ReactNode }) => {
  const value = useMemo(() => buildValue(range), [range[0], range[1]]);
  return <DashboardRangeContext.Provider value={value}>{children}</DashboardRangeContext.Provider>;
};

export const useDashboardRange = () => useContext(DashboardRangeContext);
